import { useApp, useInput } from '@libs/hooks';
import { AssetsSvgMap, TWidget, TWidgetProps } from '@libs/types';
import { camelCaseToKebabCase, ConditionalRender, getFieldName } from '@libs/utils';
import React, { useId } from 'react';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { TConsumerItem, TConsumerListData, TDropdownData } from '../../types/data';
import { RowEnum } from '../../types/enums';
import { TDropdownOption, TListOption } from '../../types/options';
import { ListSelect } from '../List/select/ListSelect';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import { Modal } from '../UI/Modal/Modal';
import { TextCommon } from '../UI/Text/TextCommon';

const Dropdown = ({ widget }: TWidgetProps<TDropdownData, TDropdownOption>) => {
    const id = useId();
    const data = widget.data ?? {};
    const messages = data.messages ?? {};
    const options = widget.options ?? {};
    const label = messages.label || data.label;
    const selectItemTitle = messages.selectItemTitle || label;
    const name = getFieldName(options.autofill);
    const inputAutoComplete = camelCaseToKebabCase(options.autofill ?? 'off');
    const { handleClickDropdown, isFilled, isError, value, isHidden, isDisabled, ref } = useInput(widget);
    const useAppHook = useApp();
    const hasItems = Array.isArray(widget.data.items) && widget.data.items.length > 0;

    const onSelectItem = (_wid: string, item: TConsumerItem) => {
        if (hasItems && item) {
            useAppHook.setModal(null);
            handleClickDropdown(item);
        }
    };
    const getTitleByValue = (selectedValue?: string): string => {
        if (selectedValue) {
            const title = data.items?.filter((i) => i.value === selectedValue)[0]?.title;
            return title ?? value;
        }
        return '';
    };

    const modalContent = (
        <Modal isCanCloseModal={true} onHandleClick={() => useAppHook.setModal(null)}>
            <ConditionalRender condition={!!selectItemTitle}>
                <TextCommon
                    isWidget={false}
                    uid={'header_' + widget.uid}
                    options={{ variant: 'header', margin: { top: 16, left: 24, right: 24, bottom: 32 } }}
                    content={selectItemTitle}
                />
            </ConditionalRender>
            <ListSelect
                rowId={RowEnum.SELECTOR_REGULAR}
                widget={widget as TWidget<TConsumerListData, TListOption>}
                isOpenInModal={true}
                onSelectItem={onSelectItem}
            />
        </Modal>
    );

    const openModal = () => {
        if (!isDisabled) {
            useAppHook.setModal(modalContent);
        }
    };

    return (
        <InputWrapper
            dataTest={widget.uid}
            isDropdownInput
            widget={widget}
            htmlFor={id}
            isDisabled={isDisabled}
            isHidden={isHidden}
            labelText={label}
            isError={isError}
            errorMessage={messages?.errorMessage ?? data.errorMessage}
            isFilled={isFilled}
            helperText={data.helperText}
            marginTop={options.margin?.top}
            marginRight={options.margin?.right}
            marginBottom={options.margin?.bottom}
            marginLeft={options.margin?.left}
            rightInputComponent={<SvgComponent onClick={openModal} fill={isDisabled ? 'grey_40_lc' : 'black'} name={AssetsSvgMap.ic_chevron_down} />}
        >
            <input
                ref={ref}
                id={id}
                disabled={isDisabled}
                name={name}
                autoComplete={inputAutoComplete}
                required={data.mandatory ?? false}
                value={getTitleByValue(value)}
                readOnly={true}
                onClick={openModal}
                onFocus={openModal}
                tabIndex={data.tabIndex}
                inputMode="text"
                type="text"
            />
        </InputWrapper>
    );
};
export { Dropdown };
